/**
 * Claim-owner — PUBLIC route (excluded from the resident shell, see
 * DesktopShell.tsx's noShellPath). Task 8's other half of the Create-society
 * flow: a fresh owner lands here from the invite email
 * (https://usebero.com/claim-owner?token=...), sets their own password, and
 * the API creates their MASTER_ADMIN account + mints a token pair the same
 * way /api/auth/login does. adoptTokens() persists that pair under the same
 * store keys login() uses, then session.refresh() makes the SessionProvider
 * pick up the new user before we route into their society's admin console.
 */
import { router, useLocalSearchParams } from 'expo-router';
import React from 'react';
import { type TextInput, View, useWindowDimensions } from 'react-native';

import type { SessionUser } from '@bero/api-client';
import { space } from '@bero/tokens';
import { Icon } from '@/features/account-admin/icons';
import { useT } from '@/i18n';
import type { DictKey } from '@/i18n/en';
import { api } from '@/lib/client';
import { homeFor } from '@/lib/homeFor';
import { useSession } from '@/lib/session';
import { useTheme } from '@/theme';
import { AppText, BrandLockup, Button, EmptyState, Field, Input, Screen, Skeleton } from '@/ui';

const WIDE = 900;

type PlatRole = 'SUPER_ADMIN' | 'SALES' | 'SUPPORT' | 'MARKETING';
type CheckState = 'checking' | 'invalid'
  | { fullName: string; societyName: string; platformRole: PlatRole | null };

/** Same humanised role names the console and the acting banner use. */
const ROLE_LABEL: Record<PlatRole, DictKey> = {
  SUPER_ADMIN: 'plat_role_super_admin', SALES: 'plat_role_sales',
  SUPPORT: 'plat_role_support', MARKETING: 'plat_role_marketing',
};

export default function ClaimOwnerScreen() {
  const { t } = useT();
  const { colors } = useTheme();
  const { width } = useWindowDimensions();
  const { refresh } = useSession();
  const { token: rawToken } = useLocalSearchParams<{ token?: string }>();
  const token = (Array.isArray(rawToken) ? rawToken[0] : rawToken) ?? '';
  const desktop = width >= WIDE;

  const [state, setState] = React.useState<CheckState>('checking');
  const [password, setPassword] = React.useState('');
  const [confirm, setConfirm] = React.useState('');
  const [error, setError] = React.useState('');
  const confirmRef = React.useRef<TextInput>(null);
  const [busy, setBusy] = React.useState(false);

  React.useEffect(() => {
    let cancelled = false;
    if (!token) { void Promise.resolve().then(() => setState('invalid')); return; }
    api.get<{ valid: boolean; societyName: string; fullName: string; platformRole?: PlatRole }>(
      `/api/platform/claim-owner?token=${encodeURIComponent(token)}`,
    )
      .then((body) => {
        if (cancelled) return;
        setState(body.valid
          ? { fullName: body.fullName, societyName: body.societyName, platformRole: body.platformRole ?? null }
          : 'invalid');
      })
      .catch(() => { if (!cancelled) setState('invalid'); });
    return () => { cancelled = true; };
  }, [token]);

  const submit = async () => {
    if (busy || state === 'checking' || state === 'invalid') return;
    if (password.length < 8) { setError(t('co_min')); return; }
    if (password !== confirm) { setError(t('co_mismatch')); return; }
    setError('');
    setBusy(true);
    try {
      const tokens = await api.post<{ accessToken: string; refreshToken: string; user: SessionUser }>(
        '/api/platform/claim-owner',
        { token, password },
      );
      const user = await api.adoptTokens(tokens);
      await refresh();
      router.replace(homeFor(user) as never);
    } catch (e) {
      setError((e as { error?: string })?.error ?? t('co_err_generic'));
      setBusy(false);
    }
  };

  let content: React.ReactNode;
  if (state === 'checking') {
    // Loading = Skeleton, never blank/spinner (house rule) — token validation
    // is a real network round trip, so give the form's own shape while we wait.
    content = (
      <View style={{ gap: space.s4 }}>
        <BrandLockup markHeight={30} />
        <Skeleton h={28} w="70%" style={{ marginTop: space.s4 }} />
        <Skeleton h={18} w="90%" />
        <Skeleton h={56} />
        <Skeleton h={56} />
        <Skeleton h={48} />
      </View>
    );
  } else if (state === 'invalid') {
    content = (
      <EmptyState
        icon={<Icon name="alert-triangle" size={40} color={colors.textTertiary} />}
        title={t('co_invalid_title')}
        body={t('co_invalid_body')}
        cta={t('fgp_back_to_signin')}
        onCta={() => router.replace('/login')}
      />
    );
  } else {
    content = (
      <View style={{ gap: space.s4 }}>
        <BrandLockup markHeight={30} />
        <View>
          <AppText variant="headingLg" style={{ marginTop: space.s4 }}>{t('co_title')}</AppText>
          <AppText variant="bodyMd" tone="secondary" style={{ marginTop: 8 }}>
            {state.platformRole
              ? t('co_sub_staff', { name: state.fullName, role: t(ROLE_LABEL[state.platformRole]) })
              : t('co_sub', { name: state.fullName, society: state.societyName })}
          </AppText>
        </View>
        <Field label={t('co_password')} error={error || undefined} help={!error ? t('co_min') : undefined}>
          <Input value={password} onChangeText={setPassword} revealable editable={!busy}
            returnKeyType="next" submitBehavior="submit" onSubmitEditing={() => confirmRef.current?.focus()} />
        </Field>
        <Field label={t('co_confirm')}>
          <Input ref={confirmRef} value={confirm} onChangeText={setConfirm} revealable editable={!busy}
            returnKeyType="go" onSubmitEditing={() => void submit()} />
        </Field>
        <Button title={t('co_submit')} size="lg" loading={busy} onPress={() => void submit()} />
      </View>
    );
  }

  if (desktop) {
    return (
      <Screen scroll={false}>
        <View style={{ flex: 1, alignItems: 'center', justifyContent: 'center', paddingHorizontal: space.s6 }}>
          <View style={{ width: '100%', maxWidth: 440 }}>{content}</View>
        </View>
      </Screen>
    );
  }

  return (
    <Screen scroll safeTop>
      <View style={{ paddingHorizontal: space.s4, paddingTop: space.s6, maxWidth: 560, width: '100%', alignSelf: 'center' }}>
        {content}
      </View>
    </Screen>
  );
}
